import Phaser from "phaser";
import type { StageConfig } from "../config/stages.ts";
import { TerrainHeightField } from "./TerrainHeightField.ts";
import { TERRAIN_LABEL } from "../entities/Vehicle.ts";
import { bakeTerrainChunk, terrainDeepColor } from "../art/terrainArt.ts";

const SAMPLE_STEP = 24;
const CHUNK_WIDTH = SAMPLE_STEP * 20;
const GROUND_DEPTH = 2000;

interface TerrainChunk {
  startX: number;
  endX: number;
  body: MatterJS.BodyType;
  /** Flat deep-dirt fill below the baked texture. */
  graphics: Phaser.GameObjects.Graphics;
  image: Phaser.GameObjects.Image;
  textureKey: string;
}

/**
 * Streams terrain chunks in/out around the camera. Each chunk is a set of static Matter
 * bodies built from convex quads between consecutive height samples (avoiding concave
 * polygon physics issues), plus a canvas-baked texture for the visuals (see `terrainArt.ts`).
 */
export class TerrainGenerator {
  private readonly scene: Phaser.Scene;
  private readonly stage: StageConfig;
  private readonly heightField: TerrainHeightField;
  private readonly chunks: TerrainChunk[] = [];
  private nextChunkStartX = 0;
  private furthestGeneratedX = 0;

  constructor(scene: Phaser.Scene, stage: StageConfig) {
    this.scene = scene;
    this.stage = stage;
    this.heightField = new TerrainHeightField(stage);
  }

  /** Returns the ground height (px, positive = up from baseline) at a given world x. */
  heightAt(x: number): number {
    return this.heightField.heightAt(x);
  }

  /** Call once at level start to build initial chunks around the spawn point. */
  primeAround(x: number, aheadDistance = CHUNK_WIDTH * 3): void {
    this.nextChunkStartX = Math.floor((x - CHUNK_WIDTH) / SAMPLE_STEP) * SAMPLE_STEP;
    while (this.furthestGeneratedX < x + aheadDistance) {
      this.generateNextChunk();
    }
  }

  /** Call every frame; generates ahead of and removes chunks behind the given camera x. */
  update(cameraLeftX: number, cameraRightX: number): void {
    while (this.furthestGeneratedX < cameraRightX + CHUNK_WIDTH * 2) {
      this.generateNextChunk();
    }
    const removeBefore = cameraLeftX - CHUNK_WIDTH * 3;
    for (let i = this.chunks.length - 1; i >= 0; i--) {
      const chunk = this.chunks[i];
      if (chunk && chunk.endX < removeBefore) {
        this.destroyChunk(chunk);
        this.chunks.splice(i, 1);
      }
    }
  }

  private generateNextChunk(): void {
    const startX = this.nextChunkStartX;
    const endX = startX + CHUNK_WIDTH;

    const topPoints: Phaser.Math.Vector2[] = [];
    for (let x = startX; x <= endX; x += SAMPLE_STEP) {
      const h = this.heightField.heightAt(x);
      topPoints.push(new Phaser.Math.Vector2(x, -h));
    }

    // Build one convex quad body per segment between consecutive samples so Matter never
    // has to deal with a large concave polygon.
    const segmentBodies: MatterJS.BodyType[] = [];
    const Bodies = this.scene.matter.bodies;
    for (let i = 0; i < topPoints.length - 1; i++) {
      const a = topPoints[i];
      const b = topPoints[i + 1];
      if (!a || !b) continue;
      const vertices = [
        { x: a.x, y: a.y },
        { x: b.x, y: b.y },
        { x: b.x, y: b.y + GROUND_DEPTH },
        { x: a.x, y: a.y + GROUND_DEPTH },
      ];
      const segment = Bodies.fromVertices(
        (a.x + b.x) / 2,
        (a.y + b.y) / 2 + GROUND_DEPTH / 2,
        [vertices],
        {
          isStatic: true,
          label: TERRAIN_LABEL,
          friction: this.stage.groundFriction,
        },
      );
      segmentBodies.push(segment);
    }

    const compound = this.scene.matter.body.create({
      parts: segmentBodies,
      isStatic: true,
      label: TERRAIN_LABEL,
      friction: this.stage.groundFriction,
    });
    this.scene.matter.world.add(compound);

    const art = bakeTerrainChunk(this.scene, this.stage, (x) => this.heightField.heightAt(x), startX, endX);
    const image = this.scene.add.image(art.x, art.y, art.key).setOrigin(0, 0).setDepth(1);
    const graphics = this.scene.add.graphics().setDepth(1);
    graphics.fillStyle(terrainDeepColor(this.stage), 1);
    graphics.fillRect(art.x, art.y + art.height - 2, art.width, GROUND_DEPTH);

    this.chunks.push({ startX, endX, body: compound, graphics, image, textureKey: art.key });
    this.nextChunkStartX = endX;
    this.furthestGeneratedX = endX;
  }

  private destroyChunk(chunk: TerrainChunk): void {
    // The Matter world may already be gone if the scene shut down first; its bodies went with it.
    if (this.scene.matter.world) this.scene.matter.world.remove(chunk.body);
    chunk.graphics.destroy();
    chunk.image.destroy();
    if (this.scene.textures.exists(chunk.textureKey)) this.scene.textures.remove(chunk.textureKey);
  }

  destroy(): void {
    for (const chunk of this.chunks) this.destroyChunk(chunk);
    this.chunks.length = 0;
  }
}
